import { Link, NavLink, Outlet } from "react-router-dom";
import { OrgSwitcher, SubscriptionBanner } from "@/components/OrgSwitcher";
import { useAuth } from "@/state/AuthContext";
import { hasPermission, PERMISSIONS } from "@/utils/permissions";
import { cn } from "@/lib/utils";

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    "block px-3 py-2 text-sm font-medium rounded-md transition-colors",
    isActive ? "bg-white/15 text-white" : "text-white/85 hover:text-white hover:bg-white/10",
  );

export function TopNavLayout() {
  const { user, tenant, logout } = useAuth();

  return (
    <div className="min-h-svh flex flex-col md:flex-row bg-[#e8eef3]">
      <aside className="w-full md:w-56 shrink-0 bg-[#1e3a4f] text-white shadow-md md:min-h-svh flex flex-col">
        <div className="p-4 border-b border-white/10">
          <Link to="/dashboard" className="text-lg font-bold tracking-tight block">
            InFloTrack
          </Link>
          <div className="mt-3">
            <OrgSwitcher className="w-full text-xs h-8 bg-white/10 border-white/20 text-white" />
          </div>
        </div>

        <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto" aria-label="Main menu">
          {hasPermission(user, PERMISSIONS.dashboard) && tenant?.subscriptionStatus !== "locked" && (
            <NavLink to="/dashboard" end className={navLinkClass}>
              Dashboard
            </NavLink>
          )}
          {hasPermission(user, PERMISSIONS.receipt) && (
            <NavLink to="/receipts" className={navLinkClass}>
              Receiving
            </NavLink>
          )}
          {hasPermission(user, PERMISSIONS.request) && (
            <NavLink to="/equipment-request" className={navLinkClass}>
              Shipping
            </NavLink>
          )}
          {hasPermission(user, PERMISSIONS.transactions) && (
            <NavLink to="/transactions" className={navLinkClass}>
              Transaction History
            </NavLink>
          )}
          {hasPermission(user, PERMISSIONS.checkout) && (
            <NavLink to="/equipment-checkout" className={navLinkClass}>
              Equipment Check-Out
            </NavLink>
          )}
          {hasPermission(user, PERMISSIONS.reports) && (
            <NavLink to="/reports/weekly" className={navLinkClass}>
              Reports
            </NavLink>
          )}
          {hasPermission(user, PERMISSIONS.request) && (
            <NavLink to="/closeout" className={navLinkClass}>
              Closeout
            </NavLink>
          )}
          {hasPermission(user, PERMISSIONS.systems) && (
            <NavLink to="/systems" className={navLinkClass}>
              Systems
            </NavLink>
          )}
          {hasPermission(user, PERMISSIONS.adminUsers) && (
            <NavLink to="/admin" className={navLinkClass}>
              Admin
            </NavLink>
          )}
        </nav>

        <div className="p-4 border-t border-white/10 text-xs shrink-0">
          <div className="font-medium truncate">{user?.displayName || user?.email}</div>
          <div className="text-white/70 truncate">{tenant?.name}</div>
          <div className="text-white/70">Role: {user?.supportMode ? "Support" : user?.roleLabel}</div>
          <button
            type="button"
            onClick={logout}
            className="text-white/90 underline hover:text-white mt-2"
          >
            Logout
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0 min-h-svh">
        <SubscriptionBanner />
        <main className="flex-1 p-4 md:p-6 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
